import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import '../styles/UserLogin.css';

const UserLogin = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  // Check if user is already logged in
  React.useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsLoggedIn(true);
        setUser(user);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isSignUp) {
        // Handle signup
        if (loginData.password !== loginData.confirmPassword) {
          setError('Passwords do not match.');
          setLoading(false);
          return;
        }
        
        if (loginData.password.length < 6) {
          setError('Password must be at least 6 characters long.');
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          loginData.email, 
          loginData.password
        );
        
        // Update display name if provided
        if (loginData.displayName && userCredential.user) {
          await userCredential.user.updateProfile({
            displayName: loginData.displayName
          });
        }
      } else {
        // Handle login
        await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
      }
      
      setIsOpen(false);
      setLoginData({ email: '', password: '', confirmPassword: '', displayName: '' });
      
      // Check if user came from mobile app
      const urlParams = new URLSearchParams(window.location.search);
      const fromApp = urlParams.get('fromApp');
      const redirectUrl = urlParams.get('redirectUrl');
      
      if (fromApp === 'true' && redirectUrl) {
        // Redirect back to mobile app
        window.location.href = redirectUrl;
      }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please try logging in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check your email.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up first.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }
      
      setError(errorMessage);
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setLoginData({ email: '', password: '', confirmPassword: '', displayName: '' });
  };

  if (isLoggedIn && user) {
    return (
      <div className="user-login">
        <div className="user-profile-compact">
          <div className="user-avatar-small">
            <i className="fas fa-user"></i>
          </div>
          <div className="user-name-small">{user.displayName || user.email.split('@')[0]}</div>
          <button onClick={handleLogout} className="logout-btn-small">
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-login">
      <button 
        onClick={() => setIsOpen(true)} 
        className="login-btn"
      >
        <i className="fas fa-sign-in-alt"></i>
        Login
      </button>

      {isOpen && (
        <div className="login-overlay">
        <div className="login-modal">
          <div className="login-header">
            <h3>{isSignUp ? 'Create LogiCar Account' : 'LogiCar Login'}</h3>
            <button 
              onClick={() => setIsOpen(false)} 
              className="close-btn"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {isSignUp && (
              <div className="form-group">
                <label htmlFor="displayName">Full Name</label>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={loginData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={loginData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={loginData.password}
                onChange={handleChange}
                required
                placeholder={isSignUp ? "Create a password (min 6 characters)" : "Enter your password"}
              />
            </div>

            {isSignUp && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={loginData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm your password"
                />
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  <i className={isSignUp ? "fas fa-user-plus" : "fas fa-sign-in-alt"}></i>
                  {isSignUp ? 'Create Account' : 'Sign In'}
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"} 
              <button 
                type="button" 
                onClick={toggleMode}
                className="toggle-mode-btn"
              >
                {isSignUp ? ' Sign in here' : ' Sign up here'}
              </button>
            </p>
            {!isSignUp && <p><a href="#forgot">Forgot your password?</a></p>}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default UserLogin;
